import moment from "moment";

export const buildScheduleDay = (dateInput) => {
  const date = moment(dateInput);
  if (!date.isValid()) return null;

  return {
    key: date.format("YYYY-MM-DD"),
    label: date.format("ddd"),
    date: date.format("D"),
    isToday: date.isSame(moment(), "day"),
    rawDate: date.format("YYYY-MM-DD"),
    dayName: date.format("dddd"),
  };
};

export const resolveWeekDays = (scheduleDays = []) => {
  if (scheduleDays.length) return scheduleDays;

  const start = moment().startOf("isoWeek");
  return Array.from({ length: 7 }).map((_, index) =>
    buildScheduleDay(start.clone().add(index, "day"))
  );
};

const formatTime = (value, meetdate) => {
  if (!value) return null;
  const parsed = moment(value, ["HH:mm:ss", "HH:mm", "hh:mm A"], true);
  if (parsed.isValid()) return parsed.format("hh:mm A");

  if (meetdate) {
    const combined = moment(`${meetdate} ${value}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
    if (combined.isValid()) return combined.format("hh:mm A");
  }

  return String(value);
};

export const formatScheduleTimeRange = (startTime, endTime, meetdate) => {
  const start = formatTime(startTime, meetdate);
  const end = formatTime(endTime, meetdate);
  if (start && end) return `${start} - ${end}`;
  return start || end || "";
};

const getMomentFromString = (timeString, meetdate) => {
  const date = meetdate ? moment(meetdate, "YYYY-MM-DD") : moment();
  const [hour, minute, second = "0"] = String(timeString || "00:00:00").split(":");
  return date.clone().set({ hour, minute, second });
};

export const getLiveClassStatus = ({
  current_time: currentTime,
  start_time: startTime,
  meetdate,
  end_time: endTime,
  stream_service,
  status_stream,
  rec_id,
}) => {
  if (status_stream === "recorded" || rec_id) return "recorded";

  if (String(stream_service) === "1") {
    const now = getMomentFromString(currentTime, meetdate);
    const start = getMomentFromString(startTime, meetdate);
    const end = getMomentFromString(endTime, meetdate);
    if (now.isBefore(start)) return "upcoming";
    if (now.isBetween(start, end)) return "live";
    if (now.isAfter(end)) return "ended";
    return "upcoming";
  }

  if (String(stream_service) === "2" || String(stream_service) === "3") {
    return status_stream || "upcoming";
  }

  return "upcoming";
};

export const formatScheduleTitle = (entry) => {
  const subject = entry?.subject ?? entry?.sub_name ?? entry?.subject_name;
  const topic =
    entry?.topic_name ??
    entry?.topic ??
    entry?.meeting_topic ??
    entry?.description ??
    entry?.video_title ??
    entry?.notes_title ??
    entry?.class_name ??
    entry?.title ??
    entry?.name;

  if (subject && topic) {
    const subjectText = String(subject).trim();
    const topicText = String(topic).trim();
    if (topicText.toLowerCase().includes(subjectText.toLowerCase())) {
      return topicText;
    }
    return `${subjectText.toUpperCase()} • ${topicText}`;
  }

  return topic ?? subject ?? "";
};

const normalizeDayKey = (entry) => {
  const raw = entry?.meetdate ?? entry?.date ?? entry?.homework_date ?? entry?.class_date;
  if (!raw) return "";
  const parsed = moment(raw, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(raw);
};

const buildItem = (entry, index, config) => {
  const {
    category,
    type,
    badge,
    badgeType,
    title,
    subtitle,
    instructor,
    instructorAvatar,
    isActionable = true,
  } = config;

  if (!title) return null;

  return {
    id: `${type}-${entry?.id ?? entry?.dc_id ?? entry?.video_id ?? entry?.homework_id ?? index}`,
    category,
    type,
    badge,
    badgeType,
    title,
    subtitle,
    instructor,
    instructorAvatar,
    dayKey: normalizeDayKey(entry),
    isActionable,
    payload: entry,
  };
};

const mapZoomClassEntry = (entry, index) => {
  const status = getLiveClassStatus(entry);
  const isRecorded = status === "recorded";
  const title = formatScheduleTitle({
    ...entry,
    topic: entry?.meeting_topic ?? entry?.topic ?? entry?.description,
  });
  const subtitle = formatScheduleTimeRange(
    entry?.start_time ?? entry?.class_start_time,
    entry?.end_time ?? entry?.class_end_time,
    entry?.meetdate
  );

  return buildItem(entry, index, {
    category: "classes",
    type: isRecorded ? "recorded_class" : "live_class",
    badge: isRecorded ? "Recorded Class" : "Live Class",
    badgeType: isRecorded ? "recorded" : "live",
    title,
    subtitle,
    instructor: entry?.faculty ?? entry?.faculty_name ?? entry?.by_upload ?? "",
    instructorAvatar: entry?.faculty_image ?? entry?.faculty_img ?? "",
    isActionable: isRecorded || status === "live" || status === "upcoming",
  });
};

const mapVideoEntry = (entry, index) => {
  const duration = entry?.duration ?? entry?.video_duration ?? entry?.total_duration;
  return buildItem(entry, index, {
    category: "classes",
    type: "video",
    badge: "Recorded Class",
    badgeType: "recorded",
    title: formatScheduleTitle(entry),
    subtitle: duration ? `${duration} (Duration)` : formatScheduleTimeRange(entry?.start_time, entry?.end_time, entry?.date),
    instructor: entry?.by_upload ?? entry?.faculty_name ?? entry?.faculty ?? "",
    instructorAvatar: entry?.faculty_image ?? entry?.video_thumnail ?? "",
  });
};

const mapNotesEntry = (entry, index) =>
  buildItem(entry, index, {
    category: "homework",
    type: "notes",
    badge: "Homework",
    badgeType: "homework",
    title: entry?.notes_title ?? formatScheduleTitle(entry),
    subtitle: entry?.subject ?? entry?.topic_name ?? "",
    instructor: entry?.by_upload ?? "",
    instructorAvatar: entry?.video_thumnail ?? "",
  });

const mapHomeworkEntry = (entry, index) => {
  const topics = Array.isArray(entry?.data)
    ? entry.data.map((item) => item?.topic).filter(Boolean)
    : [];
  const questionCount = Array.isArray(entry?.data) ? entry.data.length : entry?.total_questions;
  const topicLabel = topics[0] ?? entry?.subject ?? "Homework";

  return buildItem(entry, index, {
    category: "homework",
    type: "homework",
    badge: "Homework",
    badgeType: "homework",
    title: entry?.homework_title ?? entry?.title ?? `${entry?.subject || "Homework"}${entry?.status ? ` • ${entry.status}` : ""}`,
    subtitle:
      questionCount && topicLabel
        ? `${questionCount} Questions • ${topicLabel}`
        : topicLabel,
    instructor: entry?.faculty ?? "",
    instructorAvatar: "",
  });
};

const mapOfflineEntry = (entry, index) =>
  buildItem(entry, index, {
    category: "offline",
    type: "offline",
    badge: "Offline Class",
    badgeType: "default",
    title:
      entry?.topic
        ? `${entry?.subject_name || "Batch"} • ${entry.topic}`
        : `Batch - ${entry?.batch_name || "Offline"} (${entry?.subject_name || "Class"})`,
    subtitle: formatScheduleTimeRange(entry?.start_time, entry?.end_time, entry?.date),
    instructor: entry?.faculty_name ?? "",
    instructorAvatar: entry?.faculty_image ?? "",
    isActionable: false,
  });

const mapLiveClassEntry = (entry, index) => mapZoomClassEntry(entry, index);

export const mapScheduleItems = (scheduleData = {}) => {
  const items = [];

  const push = (list, mapper) => {
    if (!Array.isArray(list)) return;
    list.forEach((entry, index) => {
      const mapped = mapper(entry, index);
      if (mapped) items.push(mapped);
    });
  };

  push(scheduleData.liveClasses, mapZoomClassEntry);
  push(scheduleData.liveclass, mapLiveClassEntry);
  push(scheduleData.videos, mapVideoEntry);
  push(scheduleData.notes, mapNotesEntry);
  push(scheduleData.homework, mapHomeworkEntry);
  push(scheduleData.offline, mapOfflineEntry);

  return items;
};

export const extractSchedulePayload = (payload = {}) => ({
  videos: payload?.video ?? [],
  notes: payload?.notes ?? [],
  liveClasses: payload?.zoomclass ?? [],
  liveclass: payload?.liveclass ?? [],
  homework: payload?.homework ?? [],
  offline: payload?.offline ?? [],
});
